// Helper Functions
import {
  displayCurrency,
  displayNumber,
  rangeToCurrencyRange,
} from "@/utils/NumberUtils"
import type { Maybe } from "@/generated/graphql"

import {
  CONTRACT_STATUS_DISPLAY,
  LEGACY_CREDIT_SCORE_TO_UPDATED,
  LEGACY_PROPERTY_COUNT_TO_RANGE,
} from "@/screens/loans/utils/Loan"
import { CAPITAL_PRODUCTS } from "@/screens/loans/utils/LoanCapitalProducts"

import { $isNewLoan } from "@/stores/createLoan"
import { formatDate, formatDateWithoutConversion } from "@/utils/DateUtils"
import { toTitleCase } from "@/utils/StringUtils"

export const loanSearchPath = () => "/loans/setup" as const

export const loanAppPath = (loanId: string) =>
  `/loans/${loanId}` as `${string}:${string}`

export const loanAppDashboardPath = (loanId: string) =>
  `/loans/${loanId}/dashboard` as `${string}:${string}`

export const loanAppLoanWizard = (loanId: string) =>
  `/loans/setup?loanId=${loanId}` as `${string}:${string}`

export const loanSoftCreditPull = (loanId: string) =>
  `/loans/${loanId}/softcreditpull` as `${string}:${string}`

export const loanRehabBudget = (loanId: string) =>
  `/loans/${loanId}/rehab` as const

export const loanRequestsPath = (loanId: string) =>
  `/loans/${loanId}/requests` as `${string}:${string}`

export const getRouteForNewLoan = ({
  id,
}: {
  id: string
}): `${string}:${string}` => {
  $isNewLoan.set(true)
  return loanAppPath(id)
}

export const getPropertyCount = (
  property: string | Maybe<number> | undefined,
  defaultVal = "-"
) =>
  typeof property === "number"
    ? LEGACY_PROPERTY_COUNT_TO_RANGE[property]
    : defaultVal

export const getCreditScore = (
  score: string | undefined | null,
  defaultVal = "-"
) => (score ? LEGACY_CREDIT_SCORE_TO_UPDATED[score] : defaultVal)

export const getAvailableCash = (cash: string | undefined | null): string =>
  cash ? rangeToCurrencyRange(cash) : "-"

export const buildBorrowerDetailsItems = (
  user: {
    firstName: string
    lastName: string
  },
  loanApplication: {
    borrower?: string | null
    borrowerNames?: string[] | null
    flipsCompleted?: number | null
    rentalProperties?: number | null
    cashAvailable?: string | null
    creditScore?: string | null
  }
) => {
  const borrowerFirst =
    loanApplication?.borrower === "self"
      ? user.firstName
      : loanApplication?.borrowerNames?.[0] || "Entity"
  const borrowerLastName =
    loanApplication?.borrower === "self" ? user.lastName : null
  const borrowerDetails = borrowerLastName
    ? `${borrowerFirst} ${borrowerLastName}`
    : borrowerFirst

  return [
    {
      label: "Borrower",
      value: borrowerDetails || "-",
    },
    {
      label: "Credit Score",
      value: getCreditScore(loanApplication?.creditScore),
    },
    {
      label: "Experience",
      value: `${getPropertyCount(loanApplication?.flipsCompleted)} flips, ${getPropertyCount(
        loanApplication?.rentalProperties
      )} rentals`,
    },
    {
      label: "Est. Cash Available",
      value: getAvailableCash(loanApplication?.cashAvailable),
    },
  ]
}

export const buildContractDetailItems = (loanApplication: {
  targetCloseDate?: string | null
  contractStatus?: string | null
}) => {
  return [
    {
      label: "Target Closing",
      value: loanApplication?.targetCloseDate
        ? formatDate(loanApplication?.targetCloseDate, "M/dd/yyyy", true)
        : "-",
    },
    {
      label: "Contract Status",
      value: loanApplication?.contractStatus
        ? CONTRACT_STATUS_DISPLAY[loanApplication?.contractStatus]
        : "-",
    },
  ]
}

export const buildPropertyInfoItems = (loanApplication: {
  afterRepairValue?: number | null
  purchasePrice?: number | null
  costBasis?: number | null
  grossProfit?: number | null
}) => {
  return [
    {
      label: "Estimated ARV",
      value: displayCurrency({
        value: loanApplication?.afterRepairValue,
      }),
    },
    {
      label: "Purchase Price",
      value: displayCurrency({
        value: loanApplication?.purchasePrice,
      }),
    },
    {
      label: "Cost Basis",
      value: displayCurrency({
        value: loanApplication?.costBasis,
      }),
    },
    {
      label: "Gross Profit Potential",
      value: displayCurrency({
        value: loanApplication?.grossProfit,
      }),
    },
  ]
}

export const buildLoanDetailsItems = (loanApplication: {
  loanProduct?: string | null
  loanToCost?: number | null
  interestRate?: number | null
  originationRate?: number | null
}) => {
  return [
    {
      label: "Selected Product",
      value:
        CAPITAL_PRODUCTS.find((cp) => cp.slug === loanApplication.loanProduct)
          ?.name || "-",
    },
    {
      label: "Leverage",
      value: displayNumber({
        value: (loanApplication?.loanToCost || 0) * 100,
        suffix: "%",
        precision: 0,
      }),
    },
    {
      label: "Est. Interest Rate",
      value: displayNumber({
        value: (loanApplication?.interestRate || 0) * 100,
        suffix: "%",
        precision: 2,
      }),
    },
    {
      label: "Originaton Fee",
      value: displayNumber({
        value: (loanApplication?.originationRate || 0) * 100,
        suffix: "%",
        precision: 2,
      }),
    },
  ]
}

const selectedProductLabel = (product?: string | null | undefined): string => {
  if (product === null || product === undefined) return "-"
  if (product === "0G") return "Zero Gravity"
  if (product === "DD") return "Double Double"

  return toTitleCase(product)
}

export const buildClosedLoanInfoItems = (loanApplication: {
  postClose?: {
    loanInfo?: {
      loanAmount?: number | null
      interestRate?: string | null
      closeDate?: string | null
      product?: string | null
      originalMaturity?: string | null | undefined
      extendedMaturity?: string | null | undefined
    } | null
    [key: string]: any
  } | null
}) => {
  const loanInfo = loanApplication.postClose?.loanInfo
  if (loanInfo == null) return null // Covers both null and undefined

  const {
    loanAmount,
    interestRate,
    closeDate,
    product,
    originalMaturity,
    extendedMaturity,
  } = loanInfo

  const items = [
    {
      label: "Loan Amount",
      value: displayNumber({
        value: loanAmount ?? 0,
        prefix: "$",
        precision: 0,
      }),
    },
    {
      label: "Close Date",
      value: closeDate ? formatDateWithoutConversion(closeDate, "M/d/yy") : "-",
    },
    {
      label: "Selected Product",
      value: selectedProductLabel(product),
    },
    {
      label: "Interest Rate",
      value: interestRate,
    },
    {
      label: "Maturity (Original)",
      value: originalMaturity
        ? formatDateWithoutConversion(originalMaturity, "M/d/yy")
        : "-",
    },
  ]

  // Only add the extended maturity item if it exists
  if (extendedMaturity) {
    items.push({
      label: "Maturity (Extended)",
      value: formatDateWithoutConversion(extendedMaturity, "M/d/yy"),
    })
  }

  return items
}

export const buildClosedLoanLinks = (loanApplication: {
  postClose?: {
    extensionRequestLink?: string | null
    payoffRequestLink?: string | null
  } | null
}) => {
  // Return an empty array if there is no postClose data
  if (!loanApplication?.postClose) return []
  const { extensionRequestLink, payoffRequestLink } = loanApplication.postClose
  return [
    {
      label: "Extension request",
      url:
        extensionRequestLink ||
        "https://noteforms.com/forms/extension-request-submission-form?notionforms=1",
    },
    {
      label: "Payoff form",
      url:
        payoffRequestLink ||
        "https://noteforms.com/forms/payoff-ticket-request-form-j58dgo",
    },
  ]
}
